import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import "./Reportpage.css";
import config from "../config";

/* ============================================================
   DEVICE LIST PICKER
============================================================ */

function getDevicesFromSession() {
  try {
    const raw = sessionStorage.getItem("userData");

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    const flow = parsed?.flowdevices || [];
    const pack = parsed?.packingdevices || [];

    const combined = [...flow, ...pack];

    const list = parsed?.devices || combined;

    return list.map((d, i) => {

      const id =
        d.deviceIds ??
        d.deviceId ??
        d.id ??
        d.deviceid ??
        d.device_id ??
        i;

      return {
        deviceId: id,

        deviceName:
          d.deviceName ??
          d.name ??
          d.devicename ??
          `Device-${d.id ?? i}`,

        machinetype:
          d.machinetype ??
          d.machineName ??
          d.machineType ??
          d.type ??
          "",
      };
    });

  } catch {
    return [];
  }
}

/* ============================================================
   DATE FORMATTER
============================================================ */

function formatDMYPipeHM(val) {

  try {

    const d = new Date(val);

    if (isNaN(d.getTime())) return String(val ?? "");

    const dd = d.getDate();
    const mm = d.getMonth() + 1;
    const yyyy = d.getFullYear();

    const date = `${dd}/${mm}/${yyyy}`;

    const time = d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${date} | ${time}`;

  } catch {

    return String(val ?? "");
  }
}

function isDateKey(key) {

  const k = String(key).toLowerCase();

  return (
    k.includes("date") ||
    k.includes("time") ||
    k === "timestamp" ||
    k === "start" ||
    k === "end"
  );
}

/* ============================================================
   DEDUPE FUNCTION
============================================================ */

function dedupeRows(list) {

  if (!Array.isArray(list)) return [];

  const seen = new Set();

  const result = [];

  for (const row of list) {

    const key = JSON.stringify(row);

    if (!seen.has(key)) {
      seen.add(key);
      result.push(row);
    }
  }

  return result;
}

/* ============================================================
   HEADER FORMATTER
============================================================ */

function prettifyHeader(h) {

  if (!h) return "";

  const raw = String(h);

  const lower = raw.toLowerCase();

  const specialMap = {
    scaleused: "Scale Used",
    accumulationweight: "Accumulated Weight",
    accumulation_weight: "Accumulated Weight",
    accumulatedweight: "Accumulated Weight",
    totalweight: "Total Weight",
    total_weight: "Total Weight",
    actualweight: "Actual Weight",
    batchname: "Batch Name",
    batch_name: "Batch Name",
  };

  if (specialMap[lower]) return specialMap[lower];

  const withSpaces = raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  return withSpaces
    .split(" ")
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1).toLowerCase()
    )
    .join(" ");
}

/* ============================================================
   WEIGHT PICKER
============================================================ */

function getWeightNumber(row) {

  if (!row || typeof row !== "object") return null;

  let best = null;

  for (const [key, value] of Object.entries(row)) {

    const lower = key.toLowerCase();

    const num = Number(value);

    if (Number.isNaN(num)) continue;

    if (lower.includes("accum")) return num;

    if (!best && (
      lower.includes("total") ||
      lower.includes("weight")
    )) {
      best = num;
    }
  }

  return best;
}

/* ============================================================
   TIMESTAMP PICKER
============================================================ */

function getTimestampValue(row) {

  if (!row || typeof row !== "object") return null;

  const preferred = [
    "timestamp",
    "time",
    "ts",
    "createdAt",
    "created_at",
    "dateTime",
    "datetime",
  ];

  for (const k of preferred) {

    if (
      row[k] !== undefined &&
      row[k] !== null
    ) {
      return row[k];
    }
  }

  for (const [key, value] of Object.entries(row)) {

    if (
      isDateKey(key) &&
      value !== undefined &&
      value !== null
    ) {
      return value;
    }
  }

  return null;
}

const REPORT_EXCEL_ENDPOINT =
  `${config.BASE_URL}/report/export-excel`;

/* ============================================================
   MAIN COMPONENT
============================================================ */

const Report = () => {

  const [devices, setDevices] = useState([]);

  const [selectedDeviceName, setSelectedDeviceName] =
    useState("");

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const [batchList, setBatchList] = useState([]);

  const [selectedRow, setSelectedRow] = useState(null);

  const [viewData, setViewData] = useState([]);

  const [machineType, setMachineType] = useState("");

  /* ============================================================
     POPUP STATE
  ============================================================ */

  const [showPopup, setShowPopup] = useState(false);

  const [popupMessage, setPopupMessage] = useState("");

  const [popupType, setPopupType] = useState("error");

  const openPopup = (
    message,
    type = "error"
  ) => {

    setPopupMessage(message);

    setPopupType(type);

    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  /* ============================================================
     PRINT FUNCTION
  ============================================================ */

  const handlePrint = () => {

    if (!fromDate) {
      openPopup("Please select From Date");
      return;
    }

    if (!toDate) {
      openPopup("Please select To Date");
      return;
    }

    if (!selectedDeviceName) {
      openPopup("Please select Device");
      return;
    }

    if (!submitted) {
      openPopup("Please click Submit before printing");
      return;
    }

    const printContents =
      document.getElementById(
        "report-print-area"
      );

    if (
      !printContents ||
      printContents.innerHTML.trim() === ""
    ) {
      openPopup("No report data available for print");
      return;
    }

    let userDataJS = null;

    try {

      const raw =
        sessionStorage.getItem("userData");

      if (raw) {
        userDataJS = JSON.parse(raw);
      }

    } catch (err) {

      console.log("User data parse error:", err);
    }

    const printWindow = window.open(
      "",
      "",
      "width=1200,height=700"
    );

    if (!printWindow) {
      openPopup("Unable to open print window");
      return;
    }

    printWindow.document.write(`

      <html>

        <head>

          <title>Report Print</title>

          <style>

            @page {
              size: A4 portrait;
              margin: 10mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              font-family: Arial, sans-serif;
              padding: 10px;
              color: #000;
            }

            .print-navbar {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #ddd;
              padding-bottom: 10px;
            }

            .navbar-logo {
              width: 90px;
              height: auto;
            }

            .company-name {
              font-size: 22px;
              font-weight: bold;
              color: #0d47a1;
              text-align: center;
              flex: 1;
            }

            .report-title {
              text-align: center;
              font-size: 22px;
              font-weight: bold;
              margin-top: 10px;
              margin-bottom: 20px;
            }

            .date-section {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 14px;
              font-weight: bold;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
              font-size: 12px;
            }

            th,
            td {
              border: 1px solid #000;
              padding: 6px;
              text-align: center;
            }

            th {
              background: #1976d2 !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            tr:nth-child(even) {
              background: #f2f2f2 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            button {
              display: none !important;
            }

          </style>

        </head>

        <body>

          <div class="print-navbar">

            <img
              src="https://raisonautomation.com/wp-content/uploads/2024/06/Raison-New-Logo-3.png"
              alt="Left Logo"
              class="navbar-logo"
            />

            <div class="company-name">
              ${
                userDataJS
                  ? userDataJS.companyName
                  : ""
              }
            </div>

            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRARO6hC6s2BTUJMntxV9tN6SjhPubkI8Jvug&s"
              alt="Right Logo"
              class="navbar-logo"
            />

          </div>

          <div class="report-title">
            Device Report
          </div>

          <div class="date-section">

            <div>
              <strong>From:</strong>
              ${fromDate}
            </div>

            <div>
              <strong>To:</strong>
              ${toDate}
            </div>

          </div>

          ${printContents.innerHTML}

          <script>

            window.onload = function () {

              setTimeout(function () {

                window.print();

                window.close();

              }, 500);

            };

          </script>

        </body>

      </html>
    `);

    printWindow.document.close();
  };

  /* ============================================================
     LOAD DEVICES
  ============================================================ */

  useEffect(() => {

    const local = getDevicesFromSession();

    if (local.length) {
      setDevices(local);
      return;
    }

    (async () => {

      try {

        const res = await axios.get(
          `${config.BASE_URL}/devices/list`
        );

        const arr = Array.isArray(res.data)
          ? res.data
          : [];

        setDevices(
          arr.map((d, i) => ({
            deviceId:
              d.deviceIds ??
              d.deviceId ??
              d.id ??
              d.deviceid ??
              d.device_id ??
              i,

            deviceName:
              d.deviceName ??
              d.name ??
              d.devicename ??
              `Device-${d.id ?? i}`,

            machinetype:
              d.machinetype ??
              d.machineName ??
              d.machineType ??
              d.type ??
              "",
          }))
        );

      } catch {

        setDevices([]);
      }

    })();

  }, []);

  /* ============================================================
     FETCH BATCH LIST
  ============================================================ */

  const handleSubmit = async () => {

    if (
      !selectedDeviceName ||
      !fromDate ||
      !toDate
    ) {
      openPopup(
        "Select device and date range."
      );
      return;
    }

    const match = devices.find(
      (d) =>
        String(d.deviceName)
          .trim()
          .toLowerCase() ===
        String(selectedDeviceName)
          .trim()
          .toLowerCase()
    );

    if (!match) {
      openPopup("Device not found.");
      return;
    }

    const payload = {
      deviceId: match.deviceId,
      startDate: fromDate,
      endDate: toDate,
    };

    setIsLoading(true);

    setSubmitted(false);

    setSelectedRow(null);

    setViewData([]);

    try {

      const res = await axios.post(
        `${config.BASE_URL}/report/fetch`,
        payload
      );

      const data = res?.data;

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.batch)
        ? data.batch
        : [];

      const cleanList = dedupeRows(list);

      setBatchList(cleanList);

      setMachineType(
        data?.machinetype ||
          list?.[0]?.machinetype ||
          match.machinetype ||
          ""
      );

      setSubmitted(true);

    } catch (err) {

      console.log(err);

      openPopup(
        "Failed to fetch report."
      );

    } finally {

      setIsLoading(false);
    }
  };

  /* ============================================================
     BACK
  ============================================================ */

  const handleBack = () => {

    setSelectedRow(null);

    setViewData([]);
  };

  /* ============================================================
     FETCH VIEW DATA
  ============================================================ */

  const handleView = async (row) => {

    const match = devices.find(
      (d) =>
        String(d.deviceName)
          .trim()
          .toLowerCase() ===
        String(selectedDeviceName)
          .trim()
          .toLowerCase()
    );

    if (!match) {
      openPopup("Device not found.");
      return;
    }

    const startDate =
      row?.startDate ??
      row?.start_time ??
      row?.start_time_iso ??
      row?.start_date ??
      row?.start;

    const endDate =
      row?.endDate ??
      row?.end_time ??
      row?.end_time_iso ??
      row?.end_date ??
      row?.end;

    const batchname =
      row?.batchname ??
      row?.batchName ??
      row?.batch ??
      row?.batch_name ??
      row?.batchno ??
      row?.batchNo;

    if (
      !startDate ||
      !endDate ||
      !batchname
    ) {
      openPopup("Row incomplete.");
      return;
    }

    const payload = {
      deviceId: match.deviceId,
      startDate,
      endDate,
      batchname: String(batchname),
    };

    setIsLoading(true);

    setSelectedRow(row);

    try {

      const res = await axios.post(
        `${config.BASE_URL}/report/get-view-data`,
        payload
      );

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setViewData(list);

    } catch (err) {

      console.log(err);

      openPopup(
        "Failed to fetch view data."
      );

    } finally {

      setIsLoading(false);
    }
  };

  /* ============================================================
     EXPORT EXCEL
  ============================================================ */

  const handleExportExcel = async () => {

    if (
      !selectedDeviceName ||
      !fromDate ||
      !toDate
    ) {
      openPopup(
        "Select device and date range before exporting."
      );
      return;
    }

    const match = devices.find(
      (d) =>
        String(d.deviceName)
          .trim()
          .toLowerCase() ===
        String(selectedDeviceName)
          .trim()
          .toLowerCase()
    );

    if (!match) {
      openPopup("Device not found.");
      return;
    }

    let companyName = "Unknown";

    try {

      const raw =
        sessionStorage.getItem("userData");

      if (raw) {

        const parsed = JSON.parse(raw);

        companyName =
          parsed?.companyName ||
          parsed?.company ||
          "Unknown";
      }

    } catch {}

    const payload = {
      deviceId: match.deviceId,
      startDate:
        selectedRow?.startDate || fromDate,
      endDate:
        selectedRow?.endDate || toDate,
      deviceName: String(
        selectedDeviceName
      ),
      companyName,
    };

    try {

      setIsExporting(true);

      const res = await axios.post(
        REPORT_EXCEL_ENDPOINT,
        payload,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob(
        [res.data],
        {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const url =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      const safeName =
        selectedDeviceName.replace(
          /[^a-z0-9]/gi,
          "_"
        );

      link.href = url;

      link.download =
        `report_${safeName}_${payload.startDate}_${payload.endDate}.xlsx`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      openPopup(
        "Excel exported successfully!",
        "success"
      );

    } catch (err) {

      console.log(err);

      openPopup("Export failed.");

    } finally {

      setIsExporting(false);
    }
  };
  /* ============================================================
   NUMBER FORMATTER
============================================================ */

function formatNumber(value) {

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }

  // If decimal exists → show only 2 digits
  if (!Number.isInteger(num)) {
    return parseFloat(num.toFixed(2));
  }

  // If whole number → don't add .00
  return num;
}

  /* ============================================================
     TABLE COMPONENT
  ============================================================ */

  const GenericTable = ({ rows }) => {

    const isPackingMachine =
      (machineType || "")
        .toLowerCase()
        .includes("pack");

    if (
      !Array.isArray(rows) ||
      rows.length === 0
    ) {
      return (
        <div className="table-container">

          <table className="styled-table">

            <thead>
              <tr>
                <th>S.no</th>
                <th>NO DATA</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td />
              </tr>
            </tbody>

          </table>

        </div>
      );
    }

    const headers = Object.keys(rows[0]);

    return (

      <div className="table-container">

        <table className="styled-table">

          <thead>

            <tr>

              <th>S.no</th>

              {headers.map((h) => (
                <th key={h}>
                  {prettifyHeader(h)}
                </th>
              ))}

              {!isPackingMachine && (
                <th>Action</th>
              )}

            </tr>

          </thead>

          <tbody>

            {rows.map((r, i) => (

              <tr key={i}>

                <td>{i + 1}</td>

                {headers.map((h) => (

                  <td key={h}>

{isDateKey(h)
  ? formatDMYPipeHM(r[h])
  : h.toLowerCase() === "endofbatch"
  ? (
      r[h] === 1 ||
      r[h] === true ||
      String(r[h]).toLowerCase() === "true"
    )
      ? "True"
      : "False"
  : formatNumber(r[h])}

                  </td>

                ))}

                {!isPackingMachine && (

                  <td>

                    <button
                      onClick={() =>
                        handleView(r)
                      }
                      disabled={isLoading}
                    >
                      View
                    </button>

                  </td>

                )}

              </tr>

            ))}

          </tbody>

        </table>

      </div>
    );
  };

  /* ============================================================
   CHART
============================================================ */

const ChartOrJson = ({ data }) => {

  if (
    !Array.isArray(data) ||
    data.length === 0
  ) {
    return (
      <pre className="json-box">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }

const pts = data.map((d) => ({
  timestamp: getTimestampValue(d),

  totalWeight: Number(
    d.totalWeight ??
    d.totalweight ??
    d.accumulationWeight ??
    d.actualWeight ??
    0
  ),
}));

  return (

    <div className="chart-container-scroll">

      <div className="chart-container">

        <ResponsiveContainer
          width="100%"
          height={300}
        >

          <AreaChart
            data={pts}
            margin={{
              top: 20,
              right: 40,
              left: 40,
              bottom: 40,
            }}
          >

            <CartesianGrid
              strokeDasharray="3 3"
            />

            <XAxis
              dataKey="timestamp"
              tickFormatter={(v) =>
                formatDMYPipeHM(v)
              }
            />

            <YAxis />

            <Tooltip
              labelFormatter={(v) =>
                formatDMYPipeHM(v)
              }
            />

            <ReferenceLine
              y={0}
              stroke="grey"
              strokeDasharray="5 5"
            />

            <Area
              type="linear"
              dataKey="totalWeight"
              stroke="#ff9100"
              fill="#ff9100"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />

          </AreaChart>

        </ResponsiveContainer>

      </div>

    </div>
  );
};
  /* ============================================================
     RENDER
  ============================================================ */

  return (

    <div id="move">

      {isLoading && (

        <div className="loading-overlay">

          <div className="spinner" />

          <p>Loading…</p>

        </div>

      )}

      <div className="report-toolbar">

        <h2 className="report-heading">
          Report Data
        </h2>

        <div className="report-subtitle">

          <strong>Device:</strong>

          {" "}
          {selectedDeviceName || "—"}

          {" "}

          {machineType && (
            <em>• {machineType}</em>
          )}

        </div>

        <div className="date-filter">

          <div className="controls">

            <label>

              From Date

              <input
                type="date"
                value={fromDate}
                onChange={(e) =>
                  setFromDate(
                    e.target.value
                  )
                }
              />

            </label>

            <label>

              To Date

              <input
                type="date"
                value={toDate}
                onChange={(e) =>
                  setToDate(
                    e.target.value
                  )
                }
              />

            </label>

            <label>

              Device

              <select
                value={selectedDeviceName}
                onChange={(e) =>
                  setSelectedDeviceName(
                    e.target.value
                  )
                }
              >

                <option
                  value=""
                  disabled
                >
                  Select device…
                </option>

                {devices.map((d, i) => (

                  <option
                    key={i}
                    value={d.deviceName}
                  >
                    {d.deviceName}
                  </option>

                ))}

              </select>

            </label>

          </div>

          <div
            className="date-actions"
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "25px",
            }}
          >

            {selectedRow ? (

              <button onClick={handleBack}>
                Back
              </button>

            ) : (

              <button onClick={handleSubmit}>
                Submit
              </button>

            )}

            <button onClick={handleExportExcel}>

              {isExporting
                ? "Exporting…"
                : "Export Excel"}

            </button>

            <button onClick={handlePrint}>
              Print
            </button>

          </div>

        </div>

      </div>

      <div id="report-print-area">

        {submitted && !selectedRow && (
          <GenericTable rows={batchList} />
        )}

        {submitted && selectedRow && (

          <div className="data-card">

            <div className="data-header">

              <strong>Batch: </strong>

              <code>

                {selectedRow?.batchname ??
                  selectedRow?.batchName ??
                  selectedRow?.batch ??
                  selectedRow?.batch_name}

              </code>

            </div>

            <ChartOrJson data={viewData} />

          </div>

        )}

      </div>

      {/* =====================================================
          POPUP
      ===================================================== */}

      {showPopup && (

        <div className="popup-overlay">

          <div className="popup-box popup-animate">

            <button
              className="popup-close"
              onClick={closePopup}
            >
              ✕
            </button>

            <h2
              className={`popup-title ${
                popupType === "success"
                  ? "popup-title-success"
                  : "popup-title-failure"
              }`}
            >

              {popupType === "success"
                ? "Success"
                : "Validation"}

            </h2>

            <p className="popup-message">
              {popupMessage}
            </p>

            <div className="popup-btn-group">

              <button
                className={`popup-button ${
                  popupType === "success"
                    ? "popup-success"
                    : "popup-error"
                }`}
                onClick={closePopup}
              >
                OK
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default Report;